import Thumbnail from 'components/Thumbnail'
import { Typography } from 'components/common'
import DropdownMenu, {
  MenuItemProps
} from 'components/common/Dropdown/DropdownMenu'
import { useConfirmPromptModal } from 'components/modals/ConfirmPromptModal'
import { useCollectiveContext } from 'context/CollectiveContext'
import {
  Thread,
  User,
  useDeleteThreadMutation,
  usePinThreadMutation,
  useUpdateDiscussionBoardThreadNotificationSettingMutation
} from 'graphql/generated'
import { useCurrentUser } from 'hooks'
import { calendarTime, humanTime } from 'lib/datetime'
import { useMemo } from 'react'
import useThreadInputModal from './useThreadInputModal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBell,
  faBellSlash,
  faThumbTack,
  faThumbtack,
  faTrash
} from '@fortawesome/pro-solid-svg-icons'
import { faPencil } from '@fortawesome/pro-regular-svg-icons'
import { CategoryThreadsQuery } from 'graphql/documents'
import { LIMIT } from './useThreadComments'
import { useForumContext } from './ForumContext'
import Link from 'next/link'
import Spinner from 'components/Spinner'
import { useRouter } from 'next/router'
import { twMerge } from 'tailwind-merge'
import StyledTooltip from 'components/Tooltip/Tooltip'
import { useAnalyticsContext } from 'context/AnalyticsContext'

const BaseThreadHeader: React.FC<{
  thread: Pick<
    Thread,
    | 'id'
    | 'title'
    | 'isPinned'
    | 'publicUrl'
    | 'createdAt'
    | 'updatedAt'
    | 'body'
    | 'attachments'
    | 'notificationSettings'
  > & {
    user: Pick<User, 'id' | 'thumb' | 'displayName' | 'publicUrl'>
    pinnedBy?: Maybe<Pick<User, 'id' | 'thumb' | 'displayName' | 'publicUrl'>>
  }
  showEditButton?: boolean
}> = ({ thread, showEditButton = false }) => {
  const { collective } = useCollectiveContext()
  const { selectedCategory, selectedCategoryId } = useForumContext()
  const router = useRouter()

  const viewer = useCurrentUser()
  const { loaded: analyticsLoaded } = useAnalyticsContext()

  const [deleteThread, { loading: isDeleting }] = useDeleteThreadMutation()

  const [pinThread, { loading: isPinning }] = usePinThreadMutation()

  const [updateNotification, { loading: isUpdatingNotification }] =
    useUpdateDiscussionBoardThreadNotificationSettingMutation()

  const confirm = useConfirmPromptModal()
  const {
    modalContent: editModalContent,
    setShowModal: setShowEditModal,
    showModal: showEditModal
  } = useThreadInputModal({
    thread
  })

  const isPrivileged = collective.membership?.privileged
  const isOwner = thread.user.id === viewer.id

  const dropdownOptions = useMemo(() => {
    const options: MenuItemProps[] = [
      {
        icon: thread.notificationSettings?.allowNotifications
          ? faBellSlash
          : faBell,
        label: thread.notificationSettings?.allowNotifications
          ? 'Unfollow Thread'
          : 'Follow Thread',
        type: 'button',
        disabled: isUpdatingNotification,
        loading: isUpdatingNotification,
        onClick: () => {
          updateNotification({
            variables: {
              threadId: thread.id,
              allowNotifications:
                !thread.notificationSettings.allowNotifications
            },
            onCompleted: () => {
              if (analyticsLoaded) {
                window.analytics?.trackAll(
                  `Forum - Thread Notification - Update`,
                  {
                    allowNotification:
                      !thread.notificationSettings.allowNotifications,
                    threadId: thread.id
                  }
                )
              }
            }
          })
        }
      }
    ]

    if (showEditButton && (isPrivileged || isOwner)) {
      options.push({
        icon: faPencil,
        label: 'Edit Thread',
        type: 'button',
        onClick: () => {
          setShowEditModal(true)
        }
      })
    }

    if (isPrivileged) {
      options.push({
        icon: faThumbTack,
        iconProps: { className: 'rotate-45' },
        label: thread.isPinned ? 'Unpin Thread' : 'Pin Thread',
        type: 'button',
        disabled: isPinning,
        loading: isPinning,
        onClick: e => {
          e.preventDefault()
          e.stopPropagation()
          pinThread({
            variables: {
              threadId: thread.id,
              pinned: !thread.isPinned
            },
            refetchQueries: [
              {
                query: CategoryThreadsQuery,
                variables: {
                  categoryId: selectedCategoryId,
                  threadConnection: {
                    first: LIMIT
                  },
                  collaboratorConnection: {
                    first: 5
                  }
                }
              }
            ],
            onCompleted: () => {
              if (analyticsLoaded) {
                window.analytics?.trackAll(`Forum - Thread - Pinned`, {
                  threadId: thread.id,
                  threadTitle: thread.title,
                  pinned: !thread.isPinned
                })
              }
            }
          })
        }
      })
    }

    if (isPrivileged) {
      options.push({
        icon: faTrash,
        label: 'Delete Thread',
        type: 'button',
        loading: isDeleting,
        disabled: isDeleting,
        color: 'red',
        onClick: () => {
          confirm({
            title: 'Delete Thread',
            body: 'Are you sure you want to remove the thread?',
            onClick: e => {
              e.preventDefault()
              e.stopPropagation()
              deleteThread({
                variables: {
                  threadId: thread.id
                },
                refetchQueries: [
                  {
                    query: CategoryThreadsQuery,
                    variables: {
                      categoryId: selectedCategoryId,
                      threadConnection: {
                        first: LIMIT
                      },
                      collaboratorConnection: {
                        first: 5
                      }
                    }
                  }
                ],
                onCompleted: () => {
                  if (analyticsLoaded) {
                    window.analytics?.trackAll(`Forum - Thread - Delete`, {
                      threadId: thread.id,
                      threadTitle: thread.title
                    })
                  }
                  router.push(selectedCategory.publicUrl)
                }
              })
            },
            colorScheme: 'red'
          })
        }
      })
    }

    return options
  }, [
    thread,
    isUpdatingNotification,
    showEditButton,
    isPrivileged,
    isOwner,
    updateNotification,
    analyticsLoaded,
    setShowEditModal,
    isPinning,
    pinThread,
    selectedCategoryId,
    isDeleting,
    confirm,
    deleteThread,
    router,
    selectedCategory.publicUrl
  ])

  const loading = isDeleting

  return (
    <div>
      <div className="flex justify-between items-start gap-4">
        <Typography
          size="xl"
          component={'span'}
          fontWeight="semibold"
          className="flex flex-1 gap-4"
        >
          {thread.title}
        </Typography>

        {!selectedCategory.archived && (
          <div className="flex gap-3 items-center">
            {/* Pinning */}
            {isPinning && isPrivileged && <Spinner />}

            {!isPinning && thread.isPinned && !!thread.pinnedBy && (
              <Typography size="sm" color="gray">
                <StyledTooltip title="Unpin thread">
                  <FontAwesomeIcon
                    icon={faThumbtack}
                    size="1x"
                    className={twMerge(
                      'pr-1 italics text-yellow-500 rotate-45',
                      isPrivileged && 'cursor-pointer'
                    )}
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      isPrivileged &&
                        pinThread({
                          variables: {
                            threadId: thread.id,
                            pinned: !thread.isPinned
                          }
                        })
                    }}
                  />
                </StyledTooltip>
                {' Pinned by '}
                <Link href={thread.pinnedBy.publicUrl} className="text-blue">
                  {thread.pinnedBy.displayName}
                </Link>
              </Typography>
            )}

            {!isPinning && !thread.isPinned && isPrivileged && (
              <StyledTooltip title="Pin thread">
                <FontAwesomeIcon
                  icon={faThumbTack}
                  className={twMerge(
                    'rotate-45 text-gray-500 hover:text-gray-600',
                    isPrivileged && 'cursor-pointer'
                  )}
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    isPrivileged &&
                      pinThread({
                        variables: {
                          threadId: thread.id,
                          pinned: !thread.isPinned
                        }
                      })
                  }}
                />
              </StyledTooltip>
            )}

            {/* Notification */}
            {isUpdatingNotification ? (
              <Spinner size="sm" className="w-3" />
            ) : (
              <StyledTooltip title="Following thread">
                <FontAwesomeIcon
                  icon={faBell}
                  className={twMerge(
                    `cursor-pointer text-gray-500 hover:text-gray-600`,
                    thread.notificationSettings.allowNotifications &&
                      'text-yellow-500'
                  )}
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    updateNotification({
                      variables: {
                        threadId: thread.id,
                        allowNotifications:
                          !thread.notificationSettings.allowNotifications
                      }
                    })
                  }}
                />
              </StyledTooltip>
            )}

            {loading ? (
              <Spinner />
            ) : (
              <DropdownMenu
                buttonClassName="-ml-2"
                items={dropdownOptions}
                iconProps={{ size: 'sm' }}
              />
            )}

            {showEditModal && editModalContent}
          </div>
        )}
      </div>

      <div className="flex items-center gap-1">
        <Thumbnail
          src={thread.user.thumb}
          className="h-6 w-6 rounded-full overflow-hidden"
        />

        <Typography
          color="gray"
          size="sm"
          className="text-blue"
          href={thread.user.publicUrl}
        >
          {thread.user.displayName} {thread.user.id === viewer.id && `(You)`}
        </Typography>

        {` \u00b7 `}
        <Typography color="gray" size="sm">
          {` ${calendarTime(thread.createdAt)}`}
        </Typography>

        {` \u00b7 `}
        <Typography color="gray" size="sm">
          {` Last Activity ${humanTime(thread.updatedAt)}`}
        </Typography>
      </div>
    </div>
  )
}

export default BaseThreadHeader
